/* Token commit */
import React, { useEffect, useRef } from "react";
import { Box, Typography, Container, Button } from "@mui/material";
import { useInView } from "react-intersection-observer";
import { trackScreenView } from '../lib/gtag.js';

export default function BBFTheReckoning() {
  const hasTrackedRef = useRef(false);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });

  useEffect(() => {
    const detectPlatform = () => {
      const ua = navigator.userAgent || navigator.vendor || window.opera;
      if (/android/i.test(ua)) return 'android';
      if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return 'ios';
      return 'desktop';
    };

    if (inView && !hasTrackedRef.current) {
      const platform = detectPlatform();
      trackScreenView(`BBFTheReckoning::${platform}`);
      hasTrackedRef.current = true;
    }
  }, [inView]);

  return (
    <Box
      id="BBFTheReckoning"
      ref={ref}
      component="section"
      sx={{
        bgcolor: "#0c0c0c",
        color: "#ffffff",
        py: { xs: 6, md: 12 },
        px: 2,
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ height: "1px", bgcolor: "#666", mt: 0.5, mb: 1.5 }} />

        <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: '#ff4c4c' }}>
          The World Went Off the Rails
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: 500, mb: 4 }}>
          The time to push back has come.
        </Typography>

        {/* Section: Systemic Breakdown */}
        <Typography variant="h6" sx={{ fontWeight: 700, mt: 5, mb: 2 }}>
          Systemic Breakdown
        </Typography>

        {[
          "America had no southern border.",
          "Illegals were described as \"the REAL Americans.\"",
          "Cities and states became sanctuaries for criminals.",
          "Enemy terror cells were invited to organize and embed.",
          "Our military was hollowed out by traitors in uniform.",
          "Israel was ambushed on 10/7/23 — and Joe Biden talked about ‘junk fees’.",
          "From East Palestine to the Middle East, the wreckage went global.",
          "And under Joe Biden, America was laughed at — weakened, humiliated, and sold out."
        ].map((line, i) => (
          <Typography key={i} variant="body1" paragraph>
            {line}
          </Typography>
        ))}

        {/* Section: Not Incompetence — Intent */}
        <Typography variant="h6" sx={{ fontWeight: 700, mt: 5, mb: 2 }}>
          Not Incompetence — Intent
        </Typography>

        {[
          "This didn’t happen by accident.",
          "It wasn’t mismanagement.",
          "It was intentional.",
          "And their fingerprints are everywhere."
        ].map((line, i) => (
          <Typography key={i + 100} variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
            {line}
          </Typography>
        ))}

        {/* Section: The Network Behind It All */}
        <Typography variant="h6" sx={{ fontWeight: 700, mt: 5, mb: 2 }}>
          The Network Behind It All
        </Typography>

        {[
          "The Biden Administration didn’t just fail.",
          "It executed the collapse using a network of insiders, influence, and willful blindness.",
          "They empowered Iran.",
          "They protected Hamas.",
          "They targeted President Trump.",
          "They censored the truth.",
          "They betrayed the country they were sworn to protect."
        ].map((line, i) => (
          <Typography key={i + 200} variant="body1" paragraph>
            {line}
          </Typography>
        ))}

        {/* Section: The Turning Point */}
        <Typography variant="h6" sx={{ fontWeight: 700, mt: 5, mb: 2 }}>
          The Turning Point
        </Typography>

        <Typography variant="body1" paragraph>
          When we uncovered not one, but a total of three coordinated attempts on President Trump’s life — all under the watch of the Biden White House — we finally understood what we were looking at.
        </Typography>

        <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
          This wasn’t chaos. It was a plan.
        </Typography>

        <Typography variant="body1" paragraph>
          A plan set in motion in 2009, built to ensure that nothing — and no one — could ever break Joe Biden’s grip on power.
        </Typography>

        <Typography variant="body1" paragraph>
          And with the evidence in hand,
        </Typography>
        <Typography variant="body1" paragraph>
          we filed the criminal complaint.
        </Typography>

        {/* Section: From Corruption to Conspiracy */}
        <Box
          sx={{
            mt: 6,
            mb: 4,
            px: 3,
            py: 2,
            bgcolor: "#1a1a1a",
            borderLeft: "6px solid red",
            color: "#eee",
          }}
        >
          <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
            Because what we uncovered wasn’t just corruption.
          </Typography>
          <Typography variant="body1" paragraph>
            It was coordination.
          </Typography>
          <Typography variant="body1" paragraph>
            The Biden Administration operated like a domestic terrorist organization — weaponizing fear, silencing dissent, and targeting its opposition.
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontWeight: 'bold', mt: 2 }}>
            And with the evidence in hand,
          </Typography>
          <Typography variant="body1" paragraph>
            we jumpstarted the process — and it’s already in motion.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}